<template>
  <div class="network-tabs" @click="handlePaginationClick">
    <div class="network-tabs-header">
      <div class="network-tabs-nav" role="tablist" aria-label="Network list tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="network-tab"
          :class="{ 'is-active': activeTab === tab.key }"
          :aria-selected="activeTab === tab.key ? 'true' : 'false'"
          @click="selectTab(tab.key)"
        >
          {{ tab.label }}
          <span class="network-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <p v-if="loading" class="network-tabs-status">Loading…</p>
    </div>

    <div v-if="activeTabConfig" class="network-filter-panel">
      <div class="network-filter-panel-title">{{ activeTabConfig.kind === 'vlan' ? 'VLAN Filters' : 'VXLAN Filters' }}</div>

      <div class="network-filter-grid">
        <label class="network-filter-field">
          <span class="network-filter-label">Name</span>
          <input
            v-model="filters.name"
            type="text"
            class="form-control"
            :placeholder="activeTabConfig.kind === 'vlan' ? 'Filter VLAN names' : 'Filter VXLAN names'"
          >
        </label>

        <label v-if="activeTabConfig.kind === 'vlan'" class="network-filter-field">
          <span class="network-filter-label">VLAN ID</span>
          <input
            v-model="filters.vlan"
            type="text"
            class="form-control"
            placeholder="Filter VLAN IDs"
          >
        </label>

        <label v-if="activeTabConfig.kind === 'vlan'" class="network-filter-field">
          <span class="network-filter-label">Interface Name</span>
          <input
            v-model="filters.interfaceName"
            type="text"
            class="form-control"
            placeholder="Filter interface names"
          >
        </label>

        <label v-if="activeTabConfig.kind === 'vxlan'" class="network-filter-field">
          <span class="network-filter-label">VNI</span>
          <input
            v-model="filters.vni"
            type="text"
            class="form-control"
            placeholder="Filter VNIs"
          >
        </label>

        <div class="network-filter-actions">
          <button type="button" class="btn btn-default vm-list-vue-filter-reset network-filter-reset" @click="resetFilters">
            Reset Filters
          </button>
        </div>
      </div>
    </div>

    <div
      v-for="tab in tabs"
      :key="`panel-${tab.key}`"
      class="network-tab-panel"
      :class="{ 'is-active': activeTab === tab.key }"
      :style="{ display: activeTab === tab.key ? '' : 'none' }"
    >
      <AppPagination
        :pagination="paginationForTab(tab)"
        :aria-label="`${tab.label} pagination`"
        :key-prefix="`network-${tab.key}-page`"
      />

      <div v-if="loading && activeTab === tab.key" class="network-results-overlay">
        <div class="network-results-loader">
          <span class="network-results-spinner" aria-hidden="true"></span>
          <span>Loading network list...</span>
        </div>
      </div>

      <table v-if="tab.kind === 'vlan'" class="table table-striped table-bordered table-condensed network-table network-table--vlan">
        <thead>
          <tr>
            <th class="network-col network-col--name"><div class="text-center">Network Name</div></th>
            <th class="network-col network-col--ipv4 hidden-tablet"><div class="text-center">IPv4</div></th>
            <th class="network-col network-col--ipv6 hidden-tablet"><div class="text-center">IPv6</div></th>
            <th class="network-col network-col--vlan hidden-phone"><div class="text-center">VLAN ID</div></th>
            <th class="network-col network-col--interface"><div class="text-center">Interface</div></th>
            <th class="network-col network-col--description hidden-tablet"><div class="text-center">Description</div></th>
            <th v-if="showVlanActions" class="network-col network-col--actions network-actions-col"><div class="text-center">Actions</div></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!filteredRows(tab).length">
            <td :colspan="showVlanActions ? 7 : 6" class="text-center network-empty-state">No VLAN networks found.</td>
          </tr>
          <tr v-for="row in paginatedRows(tab)" :key="`vlan-${row.id}`">
            <td><div class="text-center"><a :href="row.show_url">{{ row.name }}</a></div></td>
            <td class="hidden-tablet"><div class="text-center">{{ row.ipv4 }}</div></td>
            <td class="hidden-tablet"><div class="text-center">{{ row.ipv6 }}</div></td>
            <td class="hidden-phone"><div class="text-center">{{ row.vlan }}</div></td>
            <td><div class="text-center">{{ row.interface_name }}</div></td>
            <td class="hidden-tablet"><div class="text-center">{{ row.description }}</div></td>
            <td v-if="showVlanActions" class="nopadding network-actions-col">
              <div class="text-center network-actions" v-if="row.actions">
                <div class="network-more-actions">
                  <button
                    type="button"
                    class="network-more-actions__gear"
                    :aria-expanded="openMenuId === `vlan-${row.id}` ? 'true' : 'false'"
                    @click.stop="toggleMenu(`vlan-${row.id}`, $event)"
                  >
                    <AppIcon name="settings" />
                    <AppIcon name="chevron-down" class="network-more-actions__caret" />
                  </button>
                  <div
                    v-if="openMenuId === `vlan-${row.id}`"
                    class="network-more-actions__menu"
                    :style="menuStyle"
                    @click.stop
                  >
                    <a :href="row.actions.edit_url" class="network-more-actions__item">
                      <AppIcon :name="row.actions.edit_icon" />
                      <span>Edit</span>
                    </a>
                    <button
                      type="button"
                      class="network-more-actions__item network-more-actions__item--danger"
                      @click="handleDelete(tab.key, row)"
                    >
                      <AppIcon name="delete" />
                      <span>{{ row.actions.destroy_label }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <table v-else class="table table-striped table-bordered table-condensed network-table network-table--vxlan">
        <thead>
          <tr>
            <th class="network-col network-col--name"><div class="text-center">Network Name</div></th>
            <th class="network-col network-col--vni"><div class="text-center">VNI</div></th>
            <th class="network-col hidden-tablet"><div class="text-center">EVPN</div></th>
            <th class="network-col network-col--owner"><div class="text-center">Owner</div></th>
            <th class="network-col network-col--description hidden-tablet"><div class="text-center">Description</div></th>
            <th class="network-col network-col--actions network-actions-col"><div class="text-center">Actions</div></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!filteredRows(tab).length">
            <td colspan="6" class="text-center network-empty-state">No VXLAN networks found.</td>
          </tr>
          <tr v-for="row in paginatedRows(tab)" :key="`vxlan-${row.id}`">
            <td><div class="text-center"><a :href="row.show_url">{{ row.name }}</a></div></td>
            <td><div class="text-center">{{ row.vni }}</div></td>
            <td class="hidden-tablet"><div v-if="row.type5_enabled" class="text-center">Type-5 · L3 VNI {{ row.l3_vni }}<span v-if="row.l3_vrf_name"> · {{ row.l3_vrf_name }}</span><br><small>{{ row.type5_prefixes.join(" · ") }}</small></div><div v-else class="text-center text-muted">L2 only</div></td>
            <td><div class="text-center">{{ row.owner }}</div></td>
            <td class="hidden-tablet"><div class="text-center">{{ row.description }}</div></td>
            <td class="nopadding network-actions-col">
              <div class="text-center network-actions" v-if="row.actions">
                <div class="network-more-actions">
                  <button
                    type="button"
                    class="network-more-actions__gear"
                    :aria-expanded="openMenuId === `vxlan-${row.id}` ? 'true' : 'false'"
                    @click.stop="toggleMenu(`vxlan-${row.id}`, $event)"
                  >
                    <AppIcon name="settings" />
                    <AppIcon name="chevron-down" class="network-more-actions__caret" />
                  </button>
                  <div
                    v-if="openMenuId === `vxlan-${row.id}`"
                    class="network-more-actions__menu"
                    :style="menuStyle"
                    @click.stop
                  >
                    <a :href="row.actions.edit_url" class="network-more-actions__item">
                      <AppIcon :name="row.actions.edit_icon" />
                      <span>Edit</span>
                    </a>
                    <button
                      type="button"
                      class="network-more-actions__item network-more-actions__item--danger"
                      @click="handleDelete(tab.key, row)"
                    >
                      <AppIcon name="delete" />
                      <span>{{ row.actions.destroy_label }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppPagination from './AppPagination.vue'
import AppIcon from './AppIcon.vue'

const props = defineProps({
  tabs: {
    type: Array,
    default: () => []
  }
})

const activeTabStorageKey = 'scc.networks.activeTab'

function initialActiveTab() {
  const fallback = props.tabs[0]?.key || ''

  try {
    const stored = window.sessionStorage.getItem(activeTabStorageKey)
    return props.tabs.some((tab) => tab.key === stored) ? stored : fallback
  } catch (_error) {
    return fallback
  }
}

function rememberActiveTab(tabKey) {
  try {
    window.sessionStorage.setItem(activeTabStorageKey, tabKey)
  } catch (_error) {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

const activeTab = ref(initialActiveTab())
const rowsByTab = ref(
  Object.fromEntries((props.tabs || []).map((tab) => [tab.key, Array.isArray(tab.rows) ? tab.rows : []]))
)
const loadedTabs = ref({})
const loading = ref(false)
const openMenuId = ref(null)
const menuStyle = ref({})
const currentPageByTab = ref(
  Object.fromEntries((props.tabs || []).map((tab) => [tab.key, 1]))
)
const perPage = 20
const filters = ref({
  name: '',
  vlan: '',
  interfaceName: '',
  vni: ''
})

const activeTabConfig = computed(() => props.tabs.find((tab) => tab.key === activeTab.value))
const showVlanActions = computed(() => (rowsByTab.value.vlan || []).some((row) => row.actions))
const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase()
}

function filteredRows(tab) {
  const rows = rowsByTab.value[tab.key] || []
  const nameKeyword = normalizeText(filters.value.name)

  if (tab.kind === 'vlan') {
    const vlanKeyword = normalizeText(filters.value.vlan)
    const interfaceKeyword = normalizeText(filters.value.interfaceName)

    return rows.filter((row) => {
      if (nameKeyword && !normalizeText(row.name).includes(nameKeyword)) return false
      if (vlanKeyword && !normalizeText(row.vlan).includes(vlanKeyword)) return false
      if (interfaceKeyword && !normalizeText(row.interface_name).includes(interfaceKeyword)) return false
      return true
    })
  }

  const vniKeyword = normalizeText(filters.value.vni)
  return rows.filter((row) => {
    if (nameKeyword && !normalizeText(row.name).includes(nameKeyword)) return false
    if (vniKeyword && !normalizeText(row.vni).includes(vniKeyword)) return false
    return true
  })
}

function paginationForTab(tab) {
  const totalCount = filteredRows(tab).length
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))
  const currentPage = Math.min(currentPageByTab.value[tab.key] || 1, totalPages)

  return {
    current_page: currentPage,
    total_pages: totalPages,
    prev_page: currentPage > 1 ? currentPage - 1 : null,
    next_page: currentPage < totalPages ? currentPage + 1 : null
  }
}

function paginatedRows(tab) {
  const rows = filteredRows(tab)
  const pagination = paginationForTab(tab)
  const offset = (pagination.current_page - 1) * perPage
  return rows.slice(offset, offset + perPage)
}

function resetFilters() {
  filters.value = {
    name: '',
    vlan: '',
    interfaceName: '',
    vni: ''
  }
}

function toggleMenu(menuId, event) {
  if (openMenuId.value === menuId) {
    openMenuId.value = null
    menuStyle.value = {}
    return
  }

  const trigger = event?.currentTarget
  const rect = trigger?.getBoundingClientRect()
  if (!rect) {
    openMenuId.value = menuId
    menuStyle.value = {}
    return
  }

  const menuWidth = 182
  const menuHeight = 112
  const gap = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let left = rect.right - menuWidth
  let top = rect.bottom + gap

  if (left + menuWidth > viewportWidth - 12) left = viewportWidth - menuWidth - 12
  if (left < 12) left = 12
  if (top + menuHeight > viewportHeight - 12) top = rect.top - menuHeight - gap
  if (top < 12) top = 12

  openMenuId.value = menuId
  menuStyle.value = { position: 'fixed', top: `${top}px`, left: `${left}px` }
}

function closeMenu() {
  openMenuId.value = null
  menuStyle.value = {}
}

async function handleDelete(tabKey, row) {
  const actions = row.actions || {}
  if (!actions.destroy_url) {
    return
  }

  if (actions.destroy_confirm_message && !window.confirm(actions.destroy_confirm_message)) {
    return
  }

  try {
    const payload = await requestJson(actions.destroy_url, { method: 'DELETE' })
    if (payload && payload.level && payload.message) {
      sccShowToast(payload.level, payload.message)
    }

    rowsByTab.value = {
      ...rowsByTab.value,
      [tabKey]: (rowsByTab.value[tabKey] || []).filter((entry) => String(entry.id) !== String(row.id))
    }
    closeMenu()
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to delete network.')
  }
}

async function loadTab(tabKey) {
  const tab = props.tabs.find((entry) => entry.key === tabKey)
  if (!tab?.data_url || loadedTabs.value[tabKey]) {
    return
  }

  loading.value = true
  const startedAt = Date.now()

  try {
    const response = await fetch(tab.data_url, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }

    const payload = await response.json()
    rowsByTab.value = {
      ...rowsByTab.value,
      [tabKey]: payload?.rows || []
    }
    loadedTabs.value = {
      ...loadedTabs.value,
      [tabKey]: true
    }
  } finally {
    const elapsed = Date.now() - startedAt
    if (elapsed < 250) {
      await new Promise((resolve) => window.setTimeout(resolve, 250 - elapsed))
    }
    loading.value = false
  }
}

async function requestJson(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(options.headers || {})
  }
  const fetchOptions = {
    method,
    headers,
    credentials: 'same-origin'
  }

  if (csrfToken && method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRF-Token'] = csrfToken
  }

  const response = await fetch(url, fetchOptions)
  const text = await response.text()
  let payload = null

  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    throw new Error((payload && payload.message) || text || 'Request failed.')
  }

  return payload
}

async function selectTab(tabKey) {
  if (!props.tabs.some((tab) => tab.key === tabKey)) {
    return
  }

  activeTab.value = tabKey
  rememberActiveTab(tabKey)
  currentPageByTab.value = {
    ...currentPageByTab.value,
    [tabKey]: 1
  }
  resetFilters()

  try {
    await loadTab(tabKey)
  } catch (error) {
    console.error('Failed to load network tab', error)
    window.alert('Failed to load the network list.')
  }
}

onMounted(() => {
  selectTab(activeTab.value)
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
})

function handleDocumentClick(event) {
  if (!event.target.closest('.network-more-actions__menu') && !event.target.closest('.network-more-actions__gear')) {
    closeMenu()
  }
}

function handlePaginationClick(event) {
  const link = event.target.closest('.pagination a[data-page]')
  if (!link) {
    return
  }

  event.preventDefault()
  const page = Number(link.dataset.page)
  if (!Number.isFinite(page) || page < 1 || !activeTab.value) {
    return
  }

  currentPageByTab.value = {
    ...currentPageByTab.value,
    [activeTab.value]: page
  }
}

watch(
  filters,
  () => {
    if (!activeTab.value) return
    currentPageByTab.value = {
      ...currentPageByTab.value,
      [activeTab.value]: 1
    }
  },
  { deep: true }
)
</script>

<style scoped>
.network-tabs {
  position: relative;
}

.network-tabs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.network-tabs-status {
  margin: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.network-filter-panel {
  margin-bottom: 18px;
  padding: 18px 20px;
  border: 1px solid #d9e2ec;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(241, 245, 249, 0.96));
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
}

.network-filter-panel-title {
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 14px;
}

.network-filter-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  align-items: end;
}

.network-filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.network-filter-label {
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.network-filter-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.network-table {
  table-layout: fixed;
}

.network-table--vlan .network-col--name {
  width: 170px;
}

.network-table--vxlan .network-col--name {
  width: 220px;
}

.network-table--vlan .network-col--ipv4 {
  width: 138px;
}

.network-table--vlan .network-col--ipv6 {
  width: 190px;
}

.network-table--vxlan .network-col--ipv4,
.network-table--vxlan .network-col--ipv6 {
  width: 150px;
}

.network-table--vxlan .network-col--description {
  width: 220px;
}

.network-table--vlan .network-col--description {
  width: 150px;
}

.network-col--vlan,
.network-col--vni {
  width: 90px;
}

.network-col--interface,
.network-col--owner {
  width: 110px;
}

.network-col--actions {
  width: 74px;
}

.network-more-actions {
  position: relative;
}

.network-more-actions__gear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 44px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d4dde8;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #eef3f8 100%);
  color: #4a5f75;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.network-more-actions__caret {
  font-size: 11px;
  opacity: 0.75;
}

.network-more-actions__menu {
  z-index: 60;
  min-width: 182px;
  padding: 8px;
  border: 1px solid #d9e2ec;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 20px 38px rgba(15, 23, 42, 0.16);
}

.network-more-actions__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #425466;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
}

.network-more-actions__item:hover {
  background: #eff4f9;
  color: #27445f;
  text-decoration: none;
}

.network-more-actions__item--danger {
  color: #9f1d2d;
}

.network-more-actions__item--danger:hover {
  background: #fff1f2;
  color: #7f1722;
}

.network-filter-reset {
  min-width: 120px;
  min-height: 42px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: linear-gradient(180deg, #ffffff 0%, #eef2f7 100%);
  color: #0f172a;
  font-weight: 600;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.network-filter-reset:hover,
.network-filter-reset:focus {
  color: #0f172a;
  background: linear-gradient(180deg, #ffffff 0%, #e2e8f0 100%);
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.16);
}

.network-results-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 253, 0.82);
  backdrop-filter: blur(1px);
}

.network-results-loader {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid rgba(130, 150, 175, 0.24);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #36506a;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 16px 34px rgba(122, 141, 164, 0.16);
}

.network-results-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(116, 143, 171, 0.22);
  border-top-color: #4d6d8f;
  border-radius: 999px;
  animation: network-tabs-spin 0.9s linear infinite;
}

.network-empty-state {
  color: #64748b;
  padding: 24px 12px !important;
}

@keyframes network-tabs-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
